import React, { useMemo, useState } from 'react';
import { ExclamationTriangleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Card from '../../ui/Card';
import { CancellationReport } from '../../../types/analytics';
import { downloadCsv, fmtHours, fmtNotice, fmtPct } from './format';

type KindFilter = 'all' | 'clinician' | 'client';

const Stat: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <Card className="p-4">
    <p className="text-xs uppercase tracking-wide text-clinic-muted">{label}</p>
    <p className="text-2xl font-semibold text-clinic-dark mt-1">{value}</p>
    {sub && <p className="text-xs text-clinic-muted mt-1">{sub}</p>}
  </Card>
);

const FlagBadge: React.FC = () => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-clinic-accent/15 text-clinic-accent">
    <ExclamationTriangleIcon className="h-3.5 w-3.5" /> Flagged
  </span>
);

const who = (by: string): string => (by === 'patient' ? 'Client' : by === 'clinician' ? 'Clinician' : 'Admin');

const CancellationsTab: React.FC<{ report: CancellationReport }> = ({ report: r }) => {
  const [kind, setKind] = useState<KindFilter>('all');
  const people = useMemo(
    () => r.people.filter(p => (kind === 'all' || p.kind === kind) && (p.cancellations > 0 || p.flagged)),
    [r.people, kind]
  );
  const flagged = r.people.filter(p => p.flagged);

  const exportCsv = () => downloadCsv(`clinic-studios-cancellations-${r.start}-to-${r.end}.csv`, [
    ['Name', 'Type', 'Bookings', 'Cancellations', 'Rate', `Late (inside ${r.cancellation_window_hours}h)`, `Late, last ${r.flag.days}d`,
      'Hours released', 'Hours held', 'Avg notice (h)', 'Flagged'],
    ...r.people.map(p => [p.name, p.kind, p.bookings, p.cancellations, Math.round(p.cancellation_rate * 100) / 100, p.late, p.late_last_30d,
      p.hours_released, p.hours_held, p.avg_notice_hours === null ? null : Math.round(p.avg_notice_hours), p.flagged ? 'yes' : '']),
  ]);

  return (
    <div className="space-y-6">
      <p className="text-sm text-clinic-muted">
        Bookings dated {r.start} to {r.end}. A cancellation is <strong className="text-clinic-dark">late</strong> when it lands inside the{' '}
        {r.cancellation_window_hours}-hour window (room held and billed). Anyone with {r.flag.threshold} or more late cancellations in the
        last {r.flag.days} days is flagged.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Cancellations" value={String(r.summary.cancellations)} sub={`${fmtPct(r.summary.rate)} of ${r.summary.bookings} bookings`} />
        <Stat label="Late" value={String(r.summary.late)} sub={`${fmtPct(r.summary.late_rate)} of bookings`} />
        <Stat label="Hours released" value={fmtHours(r.summary.hours_released)} sub="Freed, not billed" />
        <Stat label="Hours held" value={fmtHours(r.summary.hours_held)} sub="Kept and billed" />
        <Stat label="Flagged" value={String(r.summary.flagged)} sub={`${r.flag.threshold}+ late in ${r.flag.days} days`} />
      </div>

      {flagged.length > 0 && (
        <div className="p-4 rounded-card border border-clinic-accent/30 bg-clinic-accent/5" data-testid="flagged-banner">
          <p className="text-sm text-clinic-dark flex items-start gap-2">
            <ExclamationTriangleIcon className="h-5 w-5 text-clinic-accent flex-shrink-0" />
            <span>
              <strong>Worth a conversation:</strong>{' '}
              {flagged.map(p => `${p.name} (${p.kind}, ${p.late_last_30d} late)`).join(', ')}.
            </span>
          </p>
        </div>
      )}

      <Card className="p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h2 className="text-base font-semibold text-clinic-dark">Who is cancelling</h2>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg bg-clinic-surface-sunken p-1" role="tablist" aria-label="Filter by type">
              {(['all', 'clinician', 'client'] as KindFilter[]).map(k => (
                <button key={k} type="button" role="tab" aria-selected={kind === k} onClick={() => setKind(k)}
                  className={`px-3 py-1 text-xs font-medium rounded-md capitalize ${kind === k ? 'bg-white text-clinic-dark shadow-clinic-sm' : 'text-clinic-muted'}`}>
                  {k === 'all' ? 'Everyone' : `${k}s`}
                </button>
              ))}
            </div>
            <button type="button" onClick={exportCsv} className="inline-flex items-center gap-1 text-xs text-clinic-primary hover:text-clinic-primary-dark">
              <ArrowDownTrayIcon className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>
        {people.length === 0 ? (
          <p className="text-sm text-clinic-muted">No cancellations in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-clinic-muted">
                <tr>
                  <th className="py-2 pr-4">Name</th><th className="py-2 pr-4">Bookings</th><th className="py-2 pr-4">Cancelled</th>
                  <th className="py-2 pr-4">Rate</th><th className="py-2 pr-4">Late</th><th className="py-2 pr-4">Late, last {r.flag.days}d</th>
                  <th className="py-2 pr-4">Released</th><th className="py-2 pr-4">Held</th><th className="py-2">Avg notice</th>
                </tr>
              </thead>
              <tbody>
                {people.map(p => (
                  <tr key={`${p.kind}-${p.user_id}`} className="border-t border-clinic-primary-lighter">
                    <td className="py-2 pr-4">
                      <span className="font-medium text-clinic-dark">{p.name}</span>{' '}
                      <span className="text-xs text-clinic-muted capitalize">{p.kind}</span>{' '}
                      {p.flagged && <FlagBadge />}
                    </td>
                    <td className="py-2 pr-4">{p.bookings}</td>
                    <td className="py-2 pr-4">{p.cancellations}</td>
                    <td className="py-2 pr-4">{fmtPct(p.cancellation_rate)}</td>
                    <td className="py-2 pr-4">{p.late}</td>
                    <td className="py-2 pr-4">{p.late_last_30d}</td>
                    <td className="py-2 pr-4">{fmtHours(p.hours_released)}</td>
                    <td className="py-2 pr-4">{fmtHours(p.hours_held)}</td>
                    <td className="py-2">{fmtNotice(p.avg_notice_hours)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <h2 className="text-base font-semibold text-clinic-dark mb-3">Recent cancellations</h2>
        {r.events.length === 0 ? (
          <p className="text-sm text-clinic-muted">Nothing cancelled in this range.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-clinic-muted">
                <tr>
                  <th className="py-2 pr-4">Appointment</th><th className="py-2 pr-4">Clinician</th><th className="py-2 pr-4">Client</th>
                  <th className="py-2 pr-4">Cancelled by</th><th className="py-2 pr-4">Notice</th><th className="py-2 pr-4">Room</th><th className="py-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {r.events.slice(0, 50).map(e => (
                  <tr key={e.booking_id} className="border-t border-clinic-primary-lighter">
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {new Date(e.start_time).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                    </td>
                    <td className="py-2 pr-4">{e.clinician}</td>
                    <td className="py-2 pr-4">{e.self_booking ? <span className="text-clinic-muted">own booking{e.client ? ` (${e.client})` : ''}</span> : e.client}</td>
                    <td className="py-2 pr-4">{who(e.cancelled_by)}</td>
                    <td className="py-2 pr-4 whitespace-nowrap">
                      {fmtNotice(e.notice_hours)}{e.late && <span className="ml-1.5 text-xs text-clinic-accent font-medium">late</span>}
                    </td>
                    <td className="py-2 pr-4">{e.room_held ? 'Held, billed' : 'Released'}</td>
                    <td className="py-2 text-clinic-muted max-w-[16rem] truncate" title={e.reason || ''}>{e.reason || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default CancellationsTab;
